// Local cache for one timestamped piece of profile state, keyed by a literal localStorage key.

export type LocalPreference<T> = { value: T; updatedAt: string };

/** Storage key for a `preferences` bag entry, distinct from fields with their own dedicated key. */
export const preferenceStorageKey = (key: string) => `exopen-preference-${key}`;

export const readLocalPreference = <T>(
	storageKey: string,
): LocalPreference<T> | undefined => {
	try {
		const raw = localStorage.getItem(storageKey);
		if (!raw) {
			return undefined;
		}

		const parsed = JSON.parse(raw);
		if (typeof parsed?.updatedAt !== "string" || !("value" in parsed)) {
			return undefined;
		}
		return parsed as LocalPreference<T>;
	} catch {
		return undefined;
	}
};

export const writeLocalPreference = <T>(
	storageKey: string,
	value: T,
	updatedAt: string = new Date().toISOString(),
): void => {
	try {
		const entry: LocalPreference<T> = { value, updatedAt };
		localStorage.setItem(storageKey, JSON.stringify(entry));
	} catch {
		// Best-effort: a blocked or full store must not break the app.
		return;
	}
	notifyLocalPreferenceChange();
};

/** Removes a preference entry through the same path as a write, so subscribers (`useProfilePreference`) see the change too. */
export const removeLocalPreference = (storageKey: string): void => {
	try {
		localStorage.removeItem(storageKey);
	} catch {
		// Best-effort: a blocked store must not break the app.
		return;
	}
	notifyLocalPreferenceChange();
};

type LocalPreferenceListener = () => void;
const localPreferenceListeners = new Set<LocalPreferenceListener>();

const notifyLocalPreferenceChange = (): void => {
	for (const listener of localPreferenceListeners) {
		listener();
	}
};

/** Subscribes to every `writeLocalPreference` call, regardless of key — for `useSyncExternalStore`. */
export const subscribeToLocalPreference = (
	listener: LocalPreferenceListener,
): (() => void) => {
	localPreferenceListeners.add(listener);
	return () => localPreferenceListeners.delete(listener);
};
